import { AssessmentResults } from './AssessmentResults';
import { PhoneNumberSelector } from '../../../components/inbox/PhoneNumberSelector';
import { SMSMessagePreview } from '../../../components/inbox/SMSMessagePreview';
import { communicationError, deliveryLabel } from '../../../lib/communicationRequests';
// pages/hr/recruiting/CandidatePipelinePage.tsx
import { CandidateFollowup, PipelineWorkTools } from './RecruitingWorkflows';
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Typography,
  Button,
  Card,
  Chip,
  IconButton,
  Input,
  Avatar,
  Stack,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  Textarea,
  Select,
  Option,
} from '@mui/joy';
import {
  ArrowLeft as ArrowBackOutlined,
  User as PersonOutlined,
  Envelope as EmailOutlined,
  Phone as PhoneOutlined,
  PaperPlaneTilt as SendOutlined,
  Clock as ScheduleOutlined,
  CheckCircle as CheckCircleOutlined,
  XCircle as CancelOutlined,
  Star as StarOutlined,
  ChatCircle as MessageOutlined,
  CaretRight as ChevronRightOutlined,
  CalendarPlus as EventOutlined,
  Prohibit as NoShowOutlined,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useRecruitingCampaign,
  useCampaignCandidates,
  useCandidate,
  useAdvanceCandidate,
  useRejectCandidate,
  useSendCandidateMessage,
  useSendSchedulingLink,
  useHireCandidate,
  useCompleteInterview,
  useMarkInterviewNoShow,
} from '../../../hooks/useHr';
import InterviewScheduleModal, {
  INTERVIEW_TYPE_LABELS,
} from '../../../components/hr/InterviewScheduleModal';
import type {
  CandidateInterview,
  CandidateListItem,
  CandidateStage,
  InterviewStatus,
  InterviewType,
} from '../../../types/hr';
import toast from 'react-hot-toast';

const stageConfig: {
  key: CandidateStage;
  label: string;
  color: 'neutral' | 'warning' | 'primary' | 'success' | 'danger';
}[] = [
  { key: 'applied', label: 'Applied', color: 'neutral' },
  { key: 'qualified', label: 'Qualified', color: 'warning' },
  { key: 'screening_scheduled', label: 'Screening Scheduled', color: 'primary' },
  { key: 'screening_completed', label: 'Screening Done', color: 'primary' },
  { key: 'interview_scheduled', label: 'Interview Scheduled', color: 'primary' },
  { key: 'offer', label: 'Offer', color: 'success' },
  { key: 'hired', label: 'Hired', color: 'success' },
];

const interviewStatusConfig: Record<
  InterviewStatus,
  { label: string; color: 'primary' | 'success' | 'danger' | 'neutral' }
> = {
  scheduled: { label: 'Scheduled', color: 'primary' },
  completed: { label: 'Completed', color: 'success' },
  no_show: { label: 'No-show', color: 'danger' },
  cancelled: { label: 'Cancelled', color: 'neutral' },
  rescheduled: { label: 'Rescheduled', color: 'neutral' },
};

// Screening is the first interview; past it, the next one is the hiring decision.
const defaultInterviewType = (stage: CandidateStage): InterviewType =>
  stage === 'applied' || stage === 'qualified' || stage === 'screening_scheduled'
    ? 'screening'
    : 'final';

const scoreColor = (score: number | undefined): 'success' | 'warning' | 'danger' | 'neutral' => {
  if (!score) return 'neutral';
  if (score >= 80) return 'success';
  if (score >= 60) return 'warning';
  return 'danger';
};

interface CandidateCardProps {
  candidate: CandidateListItem;
  onSelect: () => void;
}

const CandidateCard: React.FC<CandidateCardProps> = ({ candidate, onSelect }) => {
  return (
    <Card
      variant="outlined"
      sx={{
        cursor: 'pointer',
        '&:hover': { borderColor: 'primary.300', bgcolor: 'background.level1' },
        mb: 1,
      }}
      onClick={onSelect}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Avatar size="sm">
          <PersonOutlined />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography level="title-sm" noWrap>
            {candidate.full_name}
          </Typography>
          <Typography level="body-xs" sx={{ color: 'neutral.500' }} noWrap>
            {candidate.email}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 0.5 }}>
            {candidate.recruiter_name && (
              <Typography level="body-xs">{candidate.recruiter_name}</Typography>
            )}
            {candidate.followup_date && <Chip size="sm">Follow up {candidate.followup_date}</Chip>}
            {candidate.assessment && <Chip size="sm" variant="soft" color="primary">{candidate.assessment.score == null ? `Assessment: ${candidate.assessment.status === 'submitted' ? 'Needs review' : candidate.assessment.status === 'registered' ? 'Not started' : candidate.assessment.status.replace('_', ' ')}` : `Assessment ${candidate.assessment.score}/100`}</Chip>}
            {candidate.score !== null && candidate.score !== undefined && (
              <Chip
                size="sm"
                variant="soft"
                color={scoreColor(candidate.score)}
                startDecorator={<StarOutlined size={12} />}
              >
                Resume {candidate.score}
              </Chip>
            )}
            <Typography level="body-xs" sx={{ color: 'neutral.400', flexBasis: '100%' }}>
              {new Date(candidate.applied_at).toLocaleDateString()} · {Math.max(0, Math.floor((Date.now() - new Date(candidate.stage_changed_at).getTime()) / 86400000))} days in stage
            </Typography>
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

interface CandidateDetailDrawerProps {
  candidateId: string | null;
  onClose: () => void;
  campaignId: string;
}

const CandidateDetailDrawer: React.FC<CandidateDetailDrawerProps> = ({
  candidateId,
  onClose,
  campaignId,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: candidate, isLoading } = useCandidate(candidateId || '');
  const advanceCandidate = useAdvanceCandidate();
  const rejectCandidate = useRejectCandidate();
  const sendMessage = useSendCandidateMessage();
  const sendSchedulingLink = useSendSchedulingLink();
  const hireCandidate = useHireCandidate();
  const completeInterview = useCompleteInterview();
  const markInterviewNoShow = useMarkInterviewNoShow();

  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [messageTo, setMessageTo] = useState(candidate?.phone || '');
  const [messageFrom, setMessageFrom] = useState('');
  const [existingHire, setExistingHire] = useState<{
    id: string;
    name: string;
    email: string;
    status: string;
  } | null>(null);
  const [hireModalOpen, setHireModalOpen] = useState(false);
  const [memberType, setMemberType] = useState<'w2' | '1099'>('w2');
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [completingInterview, setCompletingInterview] = useState<CandidateInterview | null>(null);
  const [interviewNotes, setInterviewNotes] = useState('');
  const [interviewDecision, setInterviewDecision] = useState<'advance' | 'reject' | 'hold'>('advance');

  if (!candidateId) return null;

  if (isLoading || !candidate) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  const handleAdvance = async (stage: CandidateStage) => {
    try {
      await advanceCandidate.mutateAsync({
        id: candidate.id,
        data: { stage },
      });
      toast.success('Candidate advanced');
    } catch {
      toast.error('Failed to advance candidate');
    }
  };

  const handleReject = async () => {
    if (!confirm('Are you sure you want to reject this candidate?')) return;
    try {
      await rejectCandidate.mutateAsync({ id: candidate.id });
      toast.success('Candidate rejected');
      onClose();
    } catch {
      toast.error('Failed to reject candidate');
    }
  };

  const handleSendMessage = async () => {
    try {
      const result = await sendMessage.mutateAsync({
        id: candidate.id,
        data: { body: messageText, from_number: messageFrom, to_number: messageTo.trim() },
      });
      toast(result.error || `Message: ${deliveryLabel(result.status)}`);
      setMessageModalOpen(false);
      setMessageText('');
    } catch (error) {
      toast.error(communicationError(error));
    }
  };

  const handleSendSchedulingLink = async () => {
    try {
      const result = await sendSchedulingLink.mutateAsync(candidate.id);
      toast(result.error || (result.status ? `Scheduling text: ${deliveryLabel(result.status)}` : 'Scheduling link generated; no text was sent.'));
    } catch (error) {
      toast.error(communicationError(error));
    }
  };

  const handleHire = async () => {
    try {
      const result = await hireCandidate.mutateAsync({
        id: candidate.id,
        data: { member_type: memberType },
      });

      // The backend picks the org's default package for this member type. Say
      // what actually happened rather than always claiming onboarding started.
      if (result?.onboarding_warning) {
        toast.success('Candidate hired and added to team');
        toast(result.onboarding_warning, { icon: '⚠️', duration: 8000 });
      } else if (result?.invitation_sent) {
        toast.success('Hired — onboarding invitation sent');
      } else if (result?.onboarding_session_id) {
        toast.success('Hired — onboarding started');
        toast('We could not email the invitation. Copy the link from the team directory.', {
          icon: '⚠️',
          duration: 8000,
        });
      } else {
        toast.success('Candidate hired and added to team');
      }

      setHireModalOpen(false);
      onClose();
    } catch (error) {
      const existing = (error as { responseData?: { existing_member?: typeof existingHire } })?.responseData?.existing_member;
      if (existing) setExistingHire(existing);
      else toast.error(error instanceof Error ? error.message : 'Failed to hire candidate');
    }
  };

  const handleCompleteInterview = async () => {
    if (!completingInterview) return;
    try {
      await completeInterview.mutateAsync({
        id: completingInterview.id,
        data: { notes: interviewNotes, decision: interviewDecision },
      });
      // The decision moves the candidate's stage, which this drawer reads from
      // the candidate detail query the hook does not invalidate.
      queryClient.invalidateQueries({ queryKey: ['candidate', candidate.id] });
      toast.success('Interview completed');
      setCompletingInterview(null);
      setInterviewNotes('');
      setInterviewDecision('advance');
    } catch {
      toast.error('Failed to complete interview');
    }
  };

  const handleInterviewNoShow = async (interview: CandidateInterview) => {
    try {
      await markInterviewNoShow.mutateAsync(interview.id);
      queryClient.invalidateQueries({ queryKey: ['candidate', candidate.id] });
      toast.success('Marked as no-show');
    } catch {
      toast.error('Failed to mark no-show');
    }
  };

  const currentStageIndex = stageConfig.findIndex((s) => s.key === candidate.stage);
  const nextStage = stageConfig[currentStageIndex + 1];

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: { xs: '100%', sm: 450 },
        height: '100vh',
        bgcolor: 'background.surface',
        borderLeft: '1px solid',
        borderColor: 'divider',
        overflow: 'auto',
        zIndex: 1200,
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography level="h4">{candidate.full_name}</Typography>
            <Chip
              size="sm"
              variant="soft"
              color={stageConfig.find((s) => s.key === candidate.stage)?.color || 'neutral'}
            >
              {stageConfig.find((s) => s.key === candidate.stage)?.label}
            </Chip>
          </Box>
          <IconButton aria-label="Close candidate details" onClick={onClose}>
            <ChevronRightOutlined />
          </IconButton>
        </Box>
      </Box>

      {/* Contact Info */}
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailOutlined size={16} color="var(--joy-palette-neutral-500)" />
            <Typography level="body-sm">{candidate.email}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneOutlined size={16} color="var(--joy-palette-neutral-500)" />
            <Typography level="body-sm">{candidate.phone}</Typography>
          </Box>
          {candidate.sms_consent && !candidate.sms_opted_out && (
            <Chip size="sm" color="success" variant="soft">
              SMS Enabled
            </Chip>
          )}
          {candidate.sms_opted_out && (
            <Chip size="sm" color="danger" variant="soft">
              SMS Opted Out
            </Chip>
          )}
        </Stack>
      </Box>

      <Box sx={{p: 2}}><AssessmentResults campaignId={candidate.campaign} candidateId={candidate.id} /></Box>

      {/* Score */}
      {candidate.score && (
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography level="title-sm" sx={{ mb: 1 }}>Resume review score (AI)</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: `${scoreColor(candidate.score.effective_score)}.softBg`,
                color: `${scoreColor(candidate.score.effective_score)}.solidBg`,
              }}
            >
              <Typography level="h4">{candidate.score.effective_score}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography level="body-sm">{candidate.score.rationale}</Typography>
            </Box>
          </Box>
          {candidate.score.strengths.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography level="body-xs" sx={{ color: 'success.600', mb: 0.5 }}>
                Strengths
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {candidate.score.strengths.map((s, i) => (
                  <Chip key={i} size="sm" variant="soft" color="success">
                    {s}
                  </Chip>
                ))}
              </Box>
            </Box>
          )}
          {candidate.score.red_flags.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography level="body-xs" sx={{ color: 'danger.600', mb: 0.5 }}>
                Red Flags
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {candidate.score.red_flags.map((f, i) => (
                  <Chip key={i} size="sm" variant="soft" color="danger">
                    {f}
                  </Chip>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Actions */}
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography level="title-sm" sx={{ mb: 1 }}>Actions</Typography>
        <Stack spacing={1}>
          {nextStage && candidate.stage !== 'hired' && candidate.stage !== 'rejected' && (
            <Button
              fullWidth
              startDecorator={<CheckCircleOutlined />}
              onClick={() => handleAdvance(nextStage.key)}
              loading={advanceCandidate.isPending}
            >
              Advance to {nextStage.label}
            </Button>
          )}
          {candidate.stage === 'qualified' && (
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              startDecorator={<ScheduleOutlined />}
              onClick={handleSendSchedulingLink}
              loading={sendSchedulingLink.isPending}
            >
              Send Scheduling Link
            </Button>
          )}
          {candidate.sms_consent && !candidate.sms_opted_out && (
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              startDecorator={<MessageOutlined />}
              onClick={() => {
                setMessageTo(candidate.phone || '');
                setMessageModalOpen(true);
              }}
            >
              Send Message
            </Button>
          )}
          {candidate.stage === 'offer' && (
            <Button
              fullWidth
              color="success"
              startDecorator={<CheckCircleOutlined />}
              onClick={() => setHireModalOpen(true)}
            >
              Hire Candidate
            </Button>
          )}
          {candidate.stage !== 'hired' && candidate.stage !== 'rejected' && (
            <Button
              fullWidth
              variant="outlined"
              color="danger"
              startDecorator={<CancelOutlined />}
              onClick={handleReject}
              loading={rejectCandidate.isPending}
            >
              Reject
            </Button>
          )}
        </Stack>
      </Box>

      {/* Interviews */}
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Typography level="title-sm">Interviews</Typography>
          {candidate.stage !== 'hired' && candidate.stage !== 'rejected' && (
            <Button
              size="sm"
              variant="outlined"
              startDecorator={<EventOutlined size={16} />}
              onClick={() => setScheduleModalOpen(true)}
            >
              Schedule
            </Button>
          )}
        </Box>
        {!candidate.interviews || candidate.interviews.length === 0 ? (
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            No interviews scheduled yet.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {candidate.interviews.map((interview) => {
              const statusInfo = interviewStatusConfig[interview.status];
              return (
                <Box
                  key={interview.id}
                  sx={{
                    p: 1.5,
                    borderRadius: 'md',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography level="title-sm">
                      {INTERVIEW_TYPE_LABELS[interview.interview_type]}
                    </Typography>
                    <Chip size="sm" variant="soft" color={statusInfo.color}>
                      {statusInfo.label}
                    </Chip>
                  </Box>
                  <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                    {new Date(interview.scheduled_at).toLocaleString()} •{' '}
                    {interview.duration_minutes} min •{' '}
                    {interview.interviewer_name || 'Unassigned'}
                  </Typography>
                  {interview.notes && (
                    <Typography level="body-sm" sx={{ mt: 0.5 }}>
                      {interview.notes}
                    </Typography>
                  )}
                  {interview.status === 'scheduled' && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button
                        size="sm"
                        startDecorator={<CheckCircleOutlined size={16} />}
                        onClick={() => {
                          setCompletingInterview(interview);
                          setInterviewNotes(interview.notes || '');
                          setInterviewDecision('advance');
                        }}
                      >
                        Complete
                      </Button>
                      <Button
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        startDecorator={<NoShowOutlined size={16} />}
                        onClick={() => handleInterviewNoShow(interview)}
                        loading={markInterviewNoShow.isPending}
                      >
                        No-show
                      </Button>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      {/* Messages */}
      {candidate.messages && candidate.messages.length > 0 && (
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography level="title-sm" sx={{ mb: 1 }}>Message History</Typography>
          <Stack spacing={1}>
            {candidate.messages.slice(-5).map((msg) => (
              <Box
                key={msg.id}
                sx={{
                  p: 1.5,
                  borderRadius: 'md',
                  bgcolor: msg.direction === 'outbound' ? 'primary.softBg' : 'neutral.softBg',
                }}
              >
                <Typography level="body-xs" sx={{ color: 'neutral.500', mb: 0.5 }}>
                  {msg.direction === 'outbound' ? 'Sent' : 'Received'} •{' '}
                  {new Date(msg.sent_at).toLocaleString()}
                </Typography>
                <Typography level="body-sm">{msg.body}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* Stage History */}
      {candidate.stage_history && candidate.stage_history.length > 0 && (
        <Box sx={{ p: 2 }}>
          <Typography level="title-sm" sx={{ mb: 1 }}>Stage History</Typography>
          <Stack spacing={1}>
            {candidate.stage_history.map((h) => (
              <Box key={h.id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'var(--brand-text, #7013f5)',
                    mt: 0.5,
                  }}
                />
                <Box>
                  <Typography level="body-sm">
                    {h.from_stage || 'New'} → {h.to_stage}
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                    {new Date(h.changed_at).toLocaleString()}
                    {h.changed_by_name && ` by ${h.changed_by_name}`}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* Send Message Modal */}
      <Modal open={messageModalOpen} onClose={() => setMessageModalOpen(false)}>
        <ModalDialog sx={{ width: 'min(95vw, 500px)', maxHeight: '90vh', overflowY: 'auto' }}>
          <ModalClose />
          <Typography level="h4">Send SMS</Typography>
          <PhoneNumberSelector smsOnly value={messageFrom} onChange={setMessageFrom} />
          <FormControl required>
            <FormLabel>Recipient phone number</FormLabel>
            <Input
              type="tel"
              value={messageTo}
              disabled={sendMessage.isPending}
              onChange={(e) => setMessageTo(e.target.value)}
            />
          </FormControl>
          <Typography level="body-xs">
            For this message only. The number entered must have texting consent.
          </Typography>
          <FormControl sx={{ mt: 2 }}>
            <FormLabel>Message</FormLabel>
            <Textarea
              minRows={3}
              maxRows={6}
              placeholder="Type your message..."
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
            />
            <Typography level="body-xs" sx={{ mt: 0.5, color: 'neutral.500' }}>
              {messageText.length} characters before business identification and opt-out instructions
            </Typography>
          </FormControl>
          <SMSMessagePreview
            toNumber={messageTo}
            fromNumber={messageFrom}
            body={messageText}
            purpose="recruiting"
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="outlined" onClick={() => setMessageModalOpen(false)}>
              Cancel
            </Button>
            <Button
              startDecorator={<SendOutlined />}
              onClick={handleSendMessage}
              loading={sendMessage.isPending}
              disabled={!messageText.trim() || !messageFrom || !messageTo.trim()}
            >
              Send
            </Button>
          </Box>
        </ModalDialog>
      </Modal>

      {/* Schedule Interview Modal — mounted only while open so it starts clean */}
      {scheduleModalOpen && (
        <InterviewScheduleModal
          open
          onClose={() => setScheduleModalOpen(false)}
          candidateId={candidate.id}
          candidateName={candidate.full_name}
          defaultType={defaultInterviewType(candidate.stage)}
        />
      )}

      {/* Complete Interview Modal */}
      <Modal open={!!completingInterview} onClose={() => setCompletingInterview(null)}>
        <ModalDialog sx={{ width: 'min(95vw, 500px)', maxHeight: '90vh', overflowY: 'auto' }}>
          <ModalClose />
          <Typography level="h4">Complete Interview</Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.500', mt: 0.5 }}>
            {completingInterview && INTERVIEW_TYPE_LABELS[completingInterview.interview_type]}
          </Typography>
          <FormControl sx={{ mt: 2 }}>
            <FormLabel>Notes</FormLabel>
            <Textarea
              minRows={3}
              maxRows={6}
              placeholder="How did it go?"
              value={interviewNotes}
              onChange={(e) => setInterviewNotes(e.target.value)}
            />
          </FormControl>
          <FormControl sx={{ mt: 2 }}>
            <FormLabel>Decision</FormLabel>
            <Select
              value={interviewDecision}
              onChange={(_, value) => value && setInterviewDecision(value)}
            >
              <Option value="advance">Advance</Option>
              <Option value="reject">Reject</Option>
              <Option value="hold">Hold</Option>
            </Select>
            <Typography level="body-xs" sx={{ mt: 0.5, color: 'neutral.500' }}>
              Advancing a screening moves the candidate to Screening Done; any other
              interview moves them to Offer. Hold leaves the stage alone.
            </Typography>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="outlined" onClick={() => setCompletingInterview(null)}>
              Cancel
            </Button>
            <Button onClick={handleCompleteInterview} loading={completeInterview.isPending}>
              Save
            </Button>
          </Box>
        </ModalDialog>
      </Modal>


      <CandidateFollowup key={candidate.id} candidate={candidate} />
      {/* Hire Modal */}
      <Modal open={hireModalOpen} onClose={() => setHireModalOpen(false)}>
        <ModalDialog sx={{ width: 'min(95vw, 500px)', maxHeight: '90vh', overflowY: 'auto' }}>
          <ModalClose />
          <Typography level="h4">Hire Candidate</Typography>
          {existingHire && (
            <Alert color="warning"><Box><Typography>{existingHire.name} already exists ({existingHire.status}). Review their personnel record and use Rehire for a returning worker.</Typography><Button component="a" href={`/hr/team?search=${encodeURIComponent(existingHire.email)}`}>Review existing team member</Button></Box></Alert>
          )}
          <Typography level="body-sm" sx={{ color: 'neutral.500', mt: 1 }}>
            This will create a team member record and start the onboarding process.
          </Typography>
          <FormControl sx={{ mt: 2 }}>
            <FormLabel>Employment Type</FormLabel>
            <Select
              value={memberType}
              onChange={(_, value) => value && setMemberType(value)}
            >
              <Option value="w2">W-2 Employee</Option>
              <Option value="1099">1099 Contractor</Option>
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="outlined" onClick={() => setHireModalOpen(false)}>
              Cancel
            </Button>
            <Button
              color="success"
              onClick={handleHire}
              loading={hireCandidate.isPending}
            >
              Hire
            </Button>
          </Box>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export const CandidatePipelinePage: React.FC = () => {
  const navigate = useNavigate();
  const { id: campaignId } = useParams<{ id: string }>();
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);

  const { data: campaign } = useRecruitingCampaign(campaignId || '');
  const [workFilters, setWorkFilters] = useState<Record<string, string>>({ search: '', recruiter: '', ordering: '-applied_at', attention: '' });
  const { data: candidates = [], isLoading } = useCampaignCandidates(campaignId || '', undefined, workFilters);

  const getCandidatesForStage = (stage: CandidateStage) => {
    return candidates.filter((c) => c.stage === stage);
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <IconButton onClick={() => navigate(`/hr/recruiting/${campaignId}`)}>
          <ArrowBackOutlined />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography level="h3">{campaign?.name || 'Pipeline'}</Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            {campaign?.role_title}
          </Typography>
        </Box>
        <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
          {candidates.length} candidates
        </Typography>
      </Box>

      <PipelineWorkTools candidates={candidates} filters={workFilters} onChange={setWorkFilters} />
      {/* Pipeline Board */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          bgcolor: 'background.level1',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            minWidth: 'max-content',
          }}
        >
          {stageConfig
            .filter((s) => s.key !== 'rejected' && s.key !== 'withdrawn')
            .map((stage) => {
              const stageCandidates = getCandidatesForStage(stage.key);
              return (
                <Box
                  key={stage.key}
                  sx={{
                    width: 280,
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: 'calc(100vh - 140px)',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1,
                      px: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography level="title-sm">{stage.label}</Typography>
                      <Chip size="sm" variant="soft" color={stage.color}>
                        {stageCandidates.length}
                      </Chip>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      overflow: 'auto',
                      bgcolor: 'background.surface',
                      borderRadius: 'md',
                      p: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {stageCandidates.length === 0 ? (
                      <Box
                        sx={{
                          p: 2,
                          textAlign: 'center',
                          color: 'neutral.400',
                        }}
                      >
                        <Typography level="body-sm">No candidates</Typography>
                      </Box>
                    ) : (
                      stageCandidates.map((candidate) => (
                        <CandidateCard
                          key={candidate.id}
                          candidate={candidate}
                          onSelect={() => setSelectedCandidateId(candidate.id)}
                        />
                      ))
                    )}
                  </Box>
                </Box>
              );
            })}
        </Box>
      </Box>

      {/* Candidate Detail Drawer */}
      {selectedCandidateId && (
        <>
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0,0,0,0.3)',
              zIndex: 1100,
            }}
            onClick={() => setSelectedCandidateId(null)}
          />
          <CandidateDetailDrawer
            candidateId={selectedCandidateId}
            onClose={() => setSelectedCandidateId(null)}
            campaignId={campaignId || ''}
          />
        </>
      )}
    </Box>
  );
};

export default CandidatePipelinePage;
